import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

let lockCount = 0;
let lockedScrollY = 0;
let bodyStyle = null;

export class DialogComponent extends Component {
  requiredRefs = ['dialog'];
  #openingFrame = 0;
  #closing = false;
  #ownsLock = false;
  #activeDialog = null;
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#activeDialog = this.refs.dialog;
    this.#activeDialog.addEventListener('cancel', this.#handleCancel);
    this.#activeDialog.addEventListener('close', this.#handleNativeClose);
    if (this.minWidth || this.maxWidth) window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    this.#generation++;
    cancelAnimationFrame(this.#openingFrame);
    this.#openingFrame = 0;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    window.removeEventListener('resize', this.#handleResize);
    const dialog = this.#activeDialog;
    dialog?.removeEventListener('cancel', this.#handleCancel);
    dialog?.removeEventListener('close', this.#handleNativeClose);
    if (dialog?.open) dialog.close();
    this.#releaseLock();
    this.#closing = false;
    super.disconnectedCallback();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;
    if ((minWidth && window.innerWidth < minWidth) || (maxWidth && window.innerWidth > maxWidth)) this.closeDialog();
  }, 50);

  #releaseLock() {
    if (!this.#ownsLock) return;
    this.#ownsLock = false;
    lockCount = Math.max(0, lockCount - 1);
    if (!lockCount && bodyStyle) {
      document.body.style.width = bodyStyle.width;
      document.body.style.position = bodyStyle.position;
      document.body.style.top = bodyStyle.top;
      bodyStyle = null;
      window.scrollTo({ top: lockedScrollY, behavior: 'auto' });
    }
  }

  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open || this.#openingFrame || this.#closing) return;
    this.#openingFrame = requestAnimationFrame(() => {
      this.#openingFrame = 0;
      if (!this.isConnected || dialog.open || this.#closing) return;
      const scrollY = window.scrollY;
      dialog.showModal();
      if (!lockCount) {
        lockedScrollY = scrollY;
        bodyStyle = { width: document.body.style.width, position: document.body.style.position, top: document.body.style.top };
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${lockedScrollY}px`;
      }
      lockCount++;
      this.#ownsLock = true;
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
      this.dispatchEvent(new DialogOpenEvent());
    });
  }

  closeDialog = async () => {
    cancelAnimationFrame(this.#openingFrame);
    this.#openingFrame = 0;
    const { dialog } = this.refs;
    if (!dialog.open || this.#closing) return;
    this.#closing = true;
    const generation = this.#generation;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';
    try {
      await onAnimationEnd(dialog, undefined, { subtree: false });
    } finally {
      if (generation !== this.#generation) return;
      dialog.close();
      dialog.classList.remove('dialog-closing');
      this.#finishClose();
    }
  };

  #finishClose() {
    const wasActive = this.#ownsLock;
    this.#releaseLock();
    this.#closing = false;
    if (wasActive) this.dispatchEvent(new DialogCloseEvent());
  }

  #handleNativeClose = () => {
    if (this.#activeDialog?.open) return;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#finishClose();
  };

  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  toggleDialog = () => {
    if (this.refs.dialog.open || this.#openingFrame) this.closeDialog();
    else this.showDialog();
  };

  #handleClick(event) {
    if (isClickedOutside(event, this.refs.dialog)) this.closeDialog();
  }

  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  }

  get minWidth() { return Number(this.getAttribute('dialog-active-min-width')); }
  get maxWidth() { return Number(this.getAttribute('dialog-active-max-width')); }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() { super(DialogOpenEvent.eventName); }
  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() { super(DialogCloseEvent.eventName); }
  static eventName = 'dialog:close';
}

document.addEventListener('toggle', (event) => {
  if (!(event.target instanceof HTMLDetailsElement) || !event.target.hasAttribute('scroll-lock')) return;
  document.documentElement.toggleAttribute('scroll-lock', !!document.querySelector('details[scroll-lock][open]'));
}, { capture: true });
